/**
 * The My Tasks calendar's view rules (z8nrz7fh6h), kept out of the components
 * so they are unit-tested: type colours, MM/YY and hour formatting, the window
 * of days, filters, which cell a card sits in, and what a drop proposes.
 *
 * Dates are tenant calendar dates (`YYYY-MM-DD`) handled through UTC parts
 * only, so nothing here shifts a day with the browser's timezone. "Today" comes
 * from the server, in the center's timezone.
 */

import type {
  ActivityCode,
  ActivitySource,
  ActivityState,
  CalendarActivity,
  CalendarResource,
  DayLoad,
  PendingTask,
} from '@/api/resourceCalendarApi'

export const ACTIVITY_CODES: ActivityCode[] = ['EQ', 'SA', 'UA', 'PR', 'IN', 'CL', 'RV', 'KT', 'QA']

/**
 * One colour per type, always shown with the two letters — never colour alone.
 * Literal class strings so Tailwind keeps them.
 */
export const ACTIVITY_STYLES: Record<
  ActivityCode,
  { badge: string; card: string; tint: string; dot: string }
> = {
  EQ: { badge: 'bg-sky-600 text-white', card: 'border-l-sky-500', tint: 'bg-sky-50 hover:bg-sky-100/80', dot: 'bg-sky-500' },
  SA: { badge: 'bg-fuchsia-600 text-white', card: 'border-l-fuchsia-500', tint: 'bg-fuchsia-50 hover:bg-fuchsia-100/80', dot: 'bg-fuchsia-500' },
  UA: {
    badge: 'bg-slate-500 text-white',
    card: 'border-l-slate-400',
    tint: 'bg-[repeating-linear-gradient(135deg,theme(colors.slate.50),theme(colors.slate.50)_6px,theme(colors.slate.100)_6px,theme(colors.slate.100)_12px)]',
    dot: 'bg-slate-400',
  },
  PR: { badge: 'bg-indigo-600 text-white', card: 'border-l-indigo-500', tint: 'bg-indigo-50 hover:bg-indigo-100/80', dot: 'bg-indigo-500' },
  IN: { badge: 'bg-amber-600 text-white', card: 'border-l-amber-500', tint: 'bg-amber-50 hover:bg-amber-100/80', dot: 'bg-amber-500' },
  CL: { badge: 'bg-emerald-600 text-white', card: 'border-l-emerald-500', tint: 'bg-emerald-50 hover:bg-emerald-100/80', dot: 'bg-emerald-500' },
  RV: { badge: 'bg-violet-600 text-white', card: 'border-l-violet-500', tint: 'bg-violet-50 hover:bg-violet-100/80', dot: 'bg-violet-500' },
  KT: { badge: 'bg-orange-600 text-white', card: 'border-l-orange-500', tint: 'bg-orange-50 hover:bg-orange-100/80', dot: 'bg-orange-500' },
  QA: { badge: 'bg-rose-600 text-white', card: 'border-l-rose-500', tint: 'bg-rose-50 hover:bg-rose-100/80', dot: 'bg-rose-500' },
}

export const STATE_LABELS: Record<ActivityState, string> = {
  PLANNED: 'Planned',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

/** Where an activity comes from, as its details name it. */
export const SOURCE_LABEL: Record<ActivitySource, string> = {
  local: 'My Tasks calendar',
  maintenance: 'Equipment work order',
  process_step: 'Procedure / form',
  support: 'Support activity (Resources)',
  unavailability: 'Unavailability (Resources)',
}

/** The tag on a card once work has moved on from planned — like Smart-QC's [St]. */
export const STATE_TAGS: Record<ActivityState, string | null> = {
  PLANNED: null,
  IN_PROGRESS: 'Started',
  COMPLETED: 'Done',
  CANCELLED: 'Cancelled',
}

/** A cell's card count: "3", or "2 open · 3" when some are finished. */
export function cellCount(acts: Pick<CalendarActivity, 'state'>[]): string {
  if (acts.length === 0) return ''
  const open = acts.filter((a) => a.state === 'PLANNED' || a.state === 'IN_PROGRESS').length
  return open === acts.length ? String(acts.length) : `${open} open · ${acts.length}`
}

// ── The task queue ───────────────────────────────────────────────────────────

export type QueueView = 'unassigned' | 'assigned' | 'all'

export interface QueueFilters {
  status: QueueView
  type?: ActivityCode
  search?: string
}

/** The queue rows that pass its tabs, type chip and search. */
export function filterQueue(tasks: PendingTask[], f: QueueFilters): PendingTask[] {
  const needle = f.search?.trim().toLowerCase() ?? ''
  return tasks.filter((t) => {
    if (f.status === 'unassigned' && t.ownerId !== null) return false
    if (f.status === 'assigned' && t.ownerId === null) return false
    if (f.type && t.type !== f.type) return false
    if (needle && !`${t.description} ${t.sourceRef ?? ''} ${t.ownerName ?? ''}`.toLowerCase().includes(needle))
      return false
    return true
  })
}

/** Counts for the queue's tabs and type chips. */
export function queueCounts(tasks: PendingTask[]): {
  unassigned: number
  assigned: number
  all: number
  byType: Partial<Record<ActivityCode, number>>
} {
  const byType: Partial<Record<ActivityCode, number>> = {}
  for (const t of tasks) byType[t.type] = (byType[t.type] ?? 0) + 1
  const unassigned = tasks.filter((t) => t.ownerId === null).length
  return { unassigned, assigned: tasks.length - unassigned, all: tasks.length, byType }
}

/** The id the Unassigned row goes by in cells, filters and drops. */
export const UNASSIGNED = '__unassigned__'

export type ViewMode = 'day' | 'week'

/** "2026-03-09" → "03/26"; no date → an em dash. */
export function formatDueMonth(date: string | null): string {
  if (!date) return '—'
  const [y, m] = date.split('-')
  return `${m}/${y.slice(2)}`
}

/** 2.5 → "2.5", 3 → "3": the expected hours as the requirement writes them. */
export function formatHours(hours: number): string {
  return String(Math.round(hours * 100) / 100)
}

/** A calendar date plus whole days, through UTC parts. */
export function isoAddDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

export const WEEK_DAYS = 7

/** The days a view shows from its first day. */
export function windowDays(from: string, mode: ViewMode): string[] {
  const n = mode === 'week' ? WEEK_DAYS : 1
  return Array.from({ length: n }, (_, i) => isoAddDays(from, i))
}

/** The first day after moving one view forward (+1) or back (−1). */
export function stepWindow(from: string, mode: ViewMode, direction: 1 | -1): string {
  return isoAddDays(from, direction * (mode === 'week' ? WEEK_DAYS : 1))
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Tue 21 Oct" for a day header. */
export function dayHeader(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  return `${WEEKDAY[t.getUTCDay()]} ${d} ${MONTH[m - 1]}`
}

export interface ActivityFilters {
  /** A user id, or UNASSIGNED. */
  resourceId?: string
  type?: ActivityCode
  state?: ActivityState
  search?: string
}

/** The cards that pass the filters. Load totals come from the server and ignore them. */
export function filterActivities(acts: CalendarActivity[], f: ActivityFilters): CalendarActivity[] {
  const needle = f.search?.trim().toLowerCase() ?? ''
  return acts.filter((a) => {
    if (f.resourceId && (a.ownerId ?? UNASSIGNED) !== f.resourceId) return false
    if (f.type && a.type !== f.type) return false
    if (f.state && a.state !== f.state) return false
    if (needle) {
      const hay = `${a.description} ${a.sourceRef ?? ''}`.toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })
}

export function cellKey(resourceId: string, date: string): string {
  return `${resourceId}|${date}`
}

/** Cards by the cell they sit in; no owner → the Unassigned row. */
export function groupByCell(acts: CalendarActivity[]): Map<string, CalendarActivity[]> {
  const out = new Map<string, CalendarActivity[]>()
  for (const a of acts) {
    const k = cellKey(a.ownerId ?? UNASSIGNED, a.date)
    const bucket = out.get(k)
    if (bucket) bucket.push(a)
    else out.set(k, [a])
  }
  return out
}

/** What dropping a card on a cell would change, or null when nothing would. */
export function proposeDrop(
  activity: CalendarActivity,
  cell: { resourceId: string; date: string },
): { date: string; ownerId: string | null } | null {
  const ownerId = cell.resourceId === UNASSIGNED ? null : cell.resourceId
  if (ownerId === activity.ownerId && cell.date === activity.date) return null
  return { date: cell.date, ownerId }
}

/** May this card be picked up? Finished work and source-managed work stay put. */
export function canDrag(activity: CalendarActivity, canManage: boolean): boolean {
  return (
    canManage &&
    activity.movable &&
    activity.state !== 'COMPLETED' &&
    activity.state !== 'CANCELLED'
  )
}

const isOpen = (a: CalendarActivity) => a.state !== 'COMPLETED' && a.state !== 'CANCELLED'

// ── Reading the week at a glance ────────────────────────────────────────────

/** A day header's parts: "Wed", 21, "Oct", and whether it falls on a weekend. */
export function dayParts(date: string): { weekday: string; day: number; month: string; weekend: boolean } {
  const [y, m, d] = date.split('-').map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return { weekday: WEEKDAY[dow], day: d, month: MONTH[m - 1], weekend: dow === 0 || dow === 6 }
}

export type LoadTone = 'ok' | 'busy' | 'over'

/** At or past this share of the day planned, a day reads as busy. */
const BUSY_AT = 0.85

/** The capacity bar under a cell: how full the day is, and how it should read. */
export function loadMeter(load: DayLoad): { percent: number; tone: LoadTone; label: string } {
  const label = `${formatHours(load.assignedHrs)} / ${formatHours(load.availableHrs)} h`
  if (load.overloaded || (load.availableHrs <= 0 && load.assignedHrs > 0))
    return { percent: 100, tone: 'over', label }
  if (load.availableHrs <= 0) return { percent: 0, tone: 'ok', label }
  const share = load.assignedHrs / load.availableHrs
  return {
    percent: Math.min(100, Math.round(share * 100)),
    tone: share >= BUSY_AT ? 'busy' : 'ok',
    label,
  }
}

/** One person's window: planned against available, and how many days overflow. */
export function weekSummary(resource: CalendarResource): {
  assignedHrs: number
  availableHrs: number
  overloadedDays: number
} {
  const round = (n: number) => Math.round(n * 100) / 100
  return {
    assignedHrs: round(resource.days.reduce((s, d) => s + d.assignedHrs, 0)),
    availableHrs: round(resource.days.reduce((s, d) => s + d.availableHrs, 0)),
    overloadedDays: resource.days.filter((d) => d.overloaded).length,
  }
}

/** The people with at least one overloaded day — who needs work moved off them. */
export function overloadedOnly(resources: CalendarResource[]): CalendarResource[] {
  return resources.filter((r) => r.days.some((d) => d.overloaded))
}

/** A day column's header totals: owned open work in hours, and what nobody has. */
export function dayTotals(
  acts: CalendarActivity[],
  date: string,
): { plannedHrs: number; unassigned: number; count: number } {
  const day = acts.filter((a) => a.date === date)
  const open = day.filter(isOpen)
  const planned = open.filter((a) => a.ownerId !== null && a.type !== 'UA')
  return {
    plannedHrs: Math.round(planned.reduce((s, a) => s + a.hours, 0) * 100) / 100,
    unassigned: open.filter((a) => a.ownerId === null).length,
    count: day.length,
  }
}

/** "09:00–11:30", "14:00", or null for work with no time of day. */
export function timeLabel(activity: Pick<CalendarActivity, 'startTime' | 'endTime'>): string | null {
  if (!activity.startTime) return null
  return activity.endTime ? `${activity.startTime}–${activity.endTime}` : activity.startTime
}

/** Late: planned after it is due, or still open with its due date gone by. */
export function isOverdue(activity: CalendarActivity, today: string): boolean {
  if (!activity.dueDate || !isOpen(activity)) return false
  return activity.date > activity.dueDate || activity.dueDate < today
}

/**
 * Soft tones for people's initials — one per person, the same every visit, so
 * a face is recognisable down a long roster. Literal classes for Tailwind.
 */
export const AVATAR_TONES = [
  'bg-sky-100 text-sky-800',
  'bg-emerald-100 text-emerald-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
  'bg-orange-100 text-orange-800',
] as const

export function avatarTone(name: string): (typeof AVATAR_TONES)[number] {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return AVATAR_TONES[h % AVATAR_TONES.length]
}

/** How many filters are narrowing the cards — the count on the Filters button. */
export function activeFilterCount(f: ActivityFilters): number {
  return [f.resourceId, f.type, f.state, f.search?.trim()].filter(Boolean).length
}

export type ShortcutAction =
  | 'prev'
  | 'next'
  | 'today'
  | 'day'
  | 'week'
  | 'search'
  | 'add'
  | 'board'
  | 'agenda'
  | 'workload'
  | 'month'

/** The calendar's keyboard shortcuts. */
export const SHORTCUTS: { keys: string; action: ShortcutAction; label: string }[] = [
  { keys: '←  →', action: 'prev', label: 'Previous / next' },
  { keys: 'T', action: 'today', label: 'Today' },
  { keys: 'D  W', action: 'day', label: 'Day / week range' },
  { keys: '1 – 4', action: 'board', label: 'Board · Agenda · Workload · Month' },
  { keys: '/', action: 'search', label: 'Search' },
  { keys: 'N', action: 'add', label: 'Plan a task' },
]

export function shortcutFor(key: string): ShortcutAction | null {
  switch (key) {
    case 'ArrowLeft':
      return 'prev'
    case 'ArrowRight':
      return 'next'
    case '1':
      return 'board'
    case '2':
      return 'agenda'
    case '3':
      return 'workload'
    case '4':
      return 'month'
    case '/':
      return 'search'
  }
  switch (key.toLowerCase()) {
    case 't':
      return 'today'
    case 'd':
      return 'day'
    case 'w':
      return 'week'
    case 'n':
      return 'add'
  }
  return null
}
